import React, { useEffect, useState } from "react";
import TableColumn from "../../components/TableColumn/TableColumn";
import api from "../../services/api";
import "./DjRequests.scss";
import { format } from "date-fns";
import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import profileDefault from "../../assets/images/profileDefault.png";
import Button from "../../components/Buttons/Button";
import AddVerifiedModal from "../../components/AddVerifiedModal/AddVerifiedModal";

// Self-serve "Become a Verified DJ" requests. Mirrors the organizer approval
// flow, but DJs are venue-independent so approve/deny is inline (no venue to
// assign). Approve flips the request to Verified DJ on the server.
const DjRequests = () => {
  const [rows, setRows] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const records = 10;
  const [adding, setAdding] = useState(false);

  const formatDate = (d) => (d ? format(new Date(d), "MMMM d, yyyy") : "-");

  const igCell = (handle) =>
    handle ? (
      <a
        href={`https://instagram.com/${handle}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#4F30EA", textDecoration: "underline" }}
      >
        @{handle}
      </a>
    ) : (
      "-"
    );

  const handleReview = async (id, action, mode = "") => {
    let reason = "";
    if (action === "disapprove") {
      const q =
        mode === "remove"
          ? "Remove this DJ? They'll lose the Verified DJ tag and guestlist access. Optional reason:"
          : "Deny this DJ request? You can add an optional reason:";
      reason = prompt(q);
      if (reason === null) return; // cancelled
    } else if (
      !window.confirm(
        "Approve this DJ? They'll get the Verified DJ tag and can create guestlists."
      )
    ) {
      return;
    }
    try {
      await api.post(`/dj-requests/review/${id}`, { action, reason: reason || "" });
      getRequests();
    } catch (error) {
      console.error("Error processing DJ request ==>", error);
      alert("Error processing request");
    }
  };

  const selectStyle = {
    background: "rgba(255, 255, 255, 0.06)",
    color: "#fff",
    border: "1px solid rgba(255, 255, 255, 0.15)",
    borderRadius: 6,
    padding: "6px 10px",
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    outline: "none",
  };

  const toRow = (r) => {
    // "Active DJ" = Approved AND flagged dj_active. An Approved row without
    // dj_active is a stale/removed DJ (e.g. old removals) and reads as Removed.
    const isActive = r.review_status === "Approved" && r.dj_active === true;
    const isPending = r.review_status === "Pending";
    const statusLabel = isPending ? "Pending" : isActive ? "Approved" : "Removed";
    return {
      Photo: (
        <img
          className="rounded"
          style={{ height: 40, width: 40, objectFit: "cover", borderRadius: 999 }}
          src={r.photo_url || profileDefault}
          alt={r.name || "DJ"}
        />
      ),
      Name: r.name,
      Instagram: igCell(r.instagram),
      Genres: r.genres && r.genres.length ? r.genres.join(", ") : "-",
      "Date Created": formatDate(r.created_at),
      Status: statusLabel,
      Actions: (
        <select
          defaultValue=""
          onChange={(e) => {
            const action = e.target.value;
            if (action === "accept") handleReview(r._id, "approve");
            else if (action === "deny") handleReview(r._id, "disapprove", "deny");
            else if (action === "remove") handleReview(r._id, "disapprove", "remove");
            e.target.value = "";
          }}
          style={selectStyle}
        >
          <option value="" disabled style={{ color: "#000" }}>Action ▾</option>
          {isPending && <option value="accept" style={{ color: "#000" }}>Accept</option>}
          {isPending && <option value="deny" style={{ color: "#000" }}>Deny</option>}
          {isActive && <option value="remove" style={{ color: "#000" }}>Remove</option>}
          {!isPending && !isActive && <option value="accept" style={{ color: "#000" }}>Re-approve</option>}
        </select>
      ),
      id: r._id,
    };
  };

  const getRequests = async () => {
    try {
      const response = await api.get(`/dj-requests`);
      const data = response.data?.data || [];
      setRows(data.map(toRow));
      setTotalPages(1);
    } catch (error) {
      console.error(`Error getting DJ requests. Error: ${error}`);
    }
  };

  useEffect(() => {
    getRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllData = async () => {
    try {
      const response = await api.get(`/dj-requests`);
      const data = response.data?.data || [];
      return data.map((r) => ({
        Name: r.name,
        Instagram: r.instagram ? `@${r.instagram}` : "-",
        Genres: r.genres && r.genres.length ? r.genres.join(", ") : "-",
        Photo: r.photo_url || "",
        "Date Created": formatDate(r.created_at),
        Status:
          r.review_status === "Pending"
            ? "Pending"
            : r.review_status === "Approved" && r.dj_active === true
            ? "Approved"
            : "Removed",
        id: r._id,
      }));
    } catch (error) {
      console.log(`Error fetching all DJ requests: ${error}`);
      return [];
    }
  };

  const columns = ["Photo", "Name", "Instagram", "Genres", "Date Created", "Status", "Actions"];

  const handleChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section className="users">
            <TableColumn
              title="DJ Requests"
              hasHeader={true}
              columns={columns}
              data={rows}
              sortableColumns={[]}
              hasExport={true}
              hasSearch={true}
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              records={records}
              inputSearchProps={{ value: searchTerm, onChange: handleChange }}
              fetchAllData={fetchAllData}
              customButton={
                <Button className="btn-outline-light" onClick={() => setAdding(true)}>
                  + Add DJ
                </Button>
              }
              onSort={() => {}}
            />
          </section>
          <AddVerifiedModal
            isOpen={adding}
            kind="dj"
            onClose={() => setAdding(false)}
            onAdded={(message) => {
              setAdding(false);
              getRequests();
              alert(message);
            }}
          />
        </article>
      </div>
    </main>
  );
};

export default DjRequests;
